import CryptoJS from 'crypto-js';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * SecureStorage - Encrypted storage for sensitive data
 *
 * Uses AES-256 encryption with store password as the key.
 * Works universally across web, iOS, and Android.
 *
 * Security model:
 * - Private keys encrypted with store password
 * - Encryption happens client-side before storage
 * - Keys never stored in plaintext
 * - Uses PBKDF2 for key derivation (10,000 iterations)
 */
class SecureStorage {
  /**
   * Derive encryption key from password using PBKDF2
   * @param {string} password - Store password
   * @param {string} salt - Salt for key derivation
   * @returns {string} Derived key
   */
  static deriveKey(password, salt) {
    return CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: 10000
    }).toString();
  }

  /**
   * Store sensitive data securely (encrypted)
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @param {string} password - Encryption password (store password)
   */
  static async setSecureItem(key, value, password) {
    try {
      if (!password) {
        // Try to get stored password
        password = await AsyncStorage.getItem('storePassword');
      }

      if (!password) {
        throw new Error('Password required for secure storage');
      }

      // Use key name as salt for deterministic key derivation
      const salt = `dijitto-${key}`;
      const derivedKey = this.deriveKey(password, salt);

      // Encrypt the value with AES-256
      const encrypted = CryptoJS.AES.encrypt(value, derivedKey).toString();

      // Store encrypted value
      await AsyncStorage.setItem(`secure_${key}`, encrypted);

      console.log(`[SecureStorage] ✅ Stored ${key} (encrypted)`);
      return true;
    } catch (error) {
      console.error(`[SecureStorage] ❌ Failed to store ${key}:`, error);
      throw error;
    }
  }

  /**
   * Retrieve sensitive data (decrypt)
   * @param {string} key - Storage key
   * @param {string} password - Decryption password (store password)
   * @returns {string|null} Decrypted value or null if not found
   */
  static async getSecureItem(key, password) {
    try {
      if (!password) {
        // Try to get stored password
        password = await AsyncStorage.getItem('storePassword');
      }

      if (!password) {
        console.warn(`[SecureStorage] ⚠️  No password available for ${key}`);
        return null;
      }

      // Get encrypted value
      const encrypted = await AsyncStorage.getItem(`secure_${key}`);
      if (!encrypted) {
        console.log(`[SecureStorage] ℹ️  No data found for ${key}`);
        return null;
      }

      // Derive the same key
      const salt = `dijitto-${key}`;
      const derivedKey = this.deriveKey(password, salt);

      // Decrypt
      const decrypted = CryptoJS.AES.decrypt(encrypted, derivedKey);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedString) {
        console.error(`[SecureStorage] ❌ Decryption failed for ${key} (wrong password?)`);
        return null;
      }

      console.log(`[SecureStorage] ✅ Retrieved ${key} (decrypted)`);
      return decryptedString;
    } catch (error) {
      console.error(`[SecureStorage] ❌ Failed to retrieve ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove sensitive data
   * @param {string} key - Storage key
   */
  static async removeSecureItem(key) {
    try {
      await AsyncStorage.removeItem(`secure_${key}`);
      console.log(`[SecureStorage] ✅ Removed ${key}`);
      return true;
    } catch (error) {
      console.error(`[SecureStorage] ❌ Failed to remove ${key}:`, error);
      return false;
    }
  }

  /**
   * Check if a secure item exists
   * @param {string} key - Storage key
   * @returns {boolean} True if item exists
   */
  static async hasSecureItem(key) {
    try {
      const value = await AsyncStorage.getItem(`secure_${key}`);
      return value !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * List all secure storage keys
   * @returns {Array<string>} Array of secure storage keys (without 'secure_' prefix)
   */
  static async listSecureKeys() {
    try {
      const allKeys = await AsyncStorage.getAllKeys();
      const secureKeys = allKeys
        .filter(key => key.startsWith('secure_'))
        .map(key => key.replace('secure_', ''));
      return secureKeys;
    } catch (error) {
      console.error('[SecureStorage] Failed to list keys:', error);
      return [];
    }
  }

  /**
   * Validate that a password can decrypt a stored item
   * @param {string} key - Storage key to test
   * @param {string} password - Password to validate
   * @returns {boolean} True if password is correct
   */
  static async validatePassword(key, password) {
    try {
      const value = await this.getSecureItem(key, password);
      return value !== null && value.length > 0;
    } catch (error) {
      return false;
    }
  }
}

export default SecureStorage;
